'use client'

import { useMockPhase } from './hooks'

const FILES = [
  { name: 'client-shoot-master.zip', meta: 'ZIP · 2.4 GB', tag: 'ZIP', color: 'var(--acid)' },
  { name: 'hero-cut-v3.mov', meta: 'MOV · 820 MB', tag: 'MOV', color: 'var(--sky)' },
  { name: 'gallery-raws.zip', meta: 'RAW · 6.2 GB', tag: 'RAW', color: 'var(--paper-2)' }
] as const

const MONO: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 500 }

interface SendMockProps {
  /** When set, the mock is scrubbed externally instead of running its own time loop. */
  phase?: number
}

export function SendMock({ phase: controlledPhase }: SendMockProps = {}) {
  const loopPhase = useMockPhase(6000, 0.95, controlledPhase !== undefined)
  const phase = controlledPhase ?? loopPhase
  const cursorX = phase < 0.18 ? 60 + (320 - 60) * (phase / 0.18) : 320
  const cursorY = phase < 0.18 ? 30 + (90 - 30) * (phase / 0.18) : 90
  const dropped = phase > 0.18
  const progress2 = dropped ? Math.min(100, ((phase - 0.18) / 0.55) * 100) : 0
  const progress3 = dropped ? Math.max(0, Math.min(100, ((phase - 0.4) / 0.5) * 100)) : 0
  const progresses = [dropped ? 100 : 0, progress2, progress3]
  const linkReady = phase > 0.92

  return (
    <div role="img" aria-label="Animation: file upload progress" style={{ position: 'relative' }}>
      <div
        style={{
          border: 'var(--border-w) solid var(--ink)',
          boxShadow: 'var(--shadow-xl)',
          background: 'var(--paper-3)',
          position: 'relative',
          overflow: 'hidden'
        }}>
        <div
          style={{
            borderBottom: '2.5px solid var(--ink)',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--paper-2)'
          }}>
          {[['var(--hot)'], ['var(--yellow)'], ['var(--acid)']].map(([c], i) => (
            <span
              key={i}
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: c,
                border: '1.5px solid var(--ink)'
              }}
            />
          ))}
          <div
            style={{
              flex: 1,
              marginLeft: 14,
              border: '2px solid var(--ink)',
              background: 'var(--paper)',
              padding: '4px 10px'
            }}>
            <span style={{ ...MONO, fontSize: 11 }}>snaptransfer.app/send</span>
          </div>
        </div>

        <div style={{ padding: 18, position: 'relative', minHeight: 300 }}>
          <div
            style={{
              border: '3px dashed var(--ink)',
              background: dropped ? 'var(--sky)' : 'var(--paper-2)',
              padding: 20,
              marginBottom: 12,
              position: 'relative',
              transition: 'background .3s'
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span
                style={{
                  width: 48,
                  height: 48,
                  border: '3px solid var(--ink)',
                  background: 'var(--paper)',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: 'var(--shadow-sm)'
                }}>
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0a0a0a"
                  strokeWidth={3}
                  strokeLinecap="square"
                  aria-hidden>
                  <path d="M4 14 L12 6 L20 14" />
                  <path d="M12 6 L12 20" />
                </svg>
              </span>
              <div>
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 800,
                    fontSize: 18,
                    letterSpacing: '-0.01em'
                  }}>
                  {dropped ? '3 files added' : 'Drop files to upload'}
                </div>
                <div style={{ ...MONO, fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                  {dropped ? 'RESUMABLE · VERIFIED · 2/3 DONE' : 'OR CLICK TO BROWSE · UP TO 250 GB'}
                </div>
              </div>
            </div>
          </div>

          {FILES.map((f, i) => (
            <div
              key={f.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr auto',
                gap: 10,
                alignItems: 'center',
                padding: '10px 12px',
                border: '2px solid var(--ink)',
                marginBottom: 6,
                background: 'var(--paper)',
                opacity: dropped ? 1 : 0.4,
                transition: 'opacity .3s'
              }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  border: '2px solid var(--ink)',
                  background: f.color,
                  display: 'grid',
                  placeItems: 'center'
                }}>
                <span style={{ ...MONO, fontSize: 9, fontWeight: 700 }}>{f.tag}</span>
              </span>
              <div>
                <div style={{ ...MONO, fontSize: 12, fontWeight: 700 }}>{f.name}</div>
                <div
                  aria-hidden
                  style={{
                    height: 5,
                    background: 'var(--paper-2)',
                    border: '1.5px solid var(--ink)',
                    marginTop: 4,
                    position: 'relative'
                  }}>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: `${progresses[i]}%`,
                      background: 'var(--ink)',
                      transition: 'width .2s linear'
                    }}
                  />
                </div>
              </div>
              <span style={{ ...MONO, fontSize: 11, opacity: 0.7 }}>{f.meta}</span>
            </div>
          ))}

          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: cursorX,
              top: cursorY,
              pointerEvents: 'none',
              transition: 'left .3s ease-out, top .3s ease-out',
              zIndex: 10
            }}>
            <svg width={18} height={22} viewBox="0 0 18 22" fill="var(--ink)" stroke="var(--paper)" strokeWidth={1.5}>
              <path d="M2 2 L2 18 L6 14 L9 20 L11 19 L8 13 L14 13 Z" />
            </svg>
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: -22,
          right: -16,
          background: linkReady ? 'var(--acid)' : 'var(--paper-2)',
          border: 'var(--border-w) solid var(--ink)',
          boxShadow: 'var(--shadow)',
          padding: '10px 14px',
          transform: 'rotate(3deg)',
          transition: 'background .3s'
        }}>
        <div style={{ ...MONO, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.7 }}>
          {linkReady ? 'Link generated' : 'Generating link...'}
        </div>
        <div style={{ ...MONO, fontSize: 13, fontWeight: 700 }}>
          {linkReady ? 'snap.tf/k7-mauve-otter' : '••••••••••••••••'}
        </div>
      </div>
    </div>
  )
}
